import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MenuLinkType } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { TargetIdField } from '@/features/link-targets';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { menuItemFormSchema, type MenuItemForm } from '../schemas/menu-item-schema';
import type { MenuNode } from '../schemas/menu-tree';

export type MenuItemPatch = Pick<
  MenuNode,
  'title' | 'linkType' | 'targetId' | 'url' | 'openInNewTab' | 'targetMissing'
>;

export interface MenuItemDialogProps {
  storeId: string;
  /** The node being edited; a fresh empty node when adding. */
  node: MenuNode;
  isNew: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (patch: MenuItemPatch) => void;
}

function ItemFormBody({
  storeId,
  node,
  isNew,
  onClose,
  onSave,
}: Omit<MenuItemDialogProps, 'open' | 'onOpenChange'> & { onClose: () => void }) {
  const { t } = useTranslation(['menus', 'common']);
  // The storefront path of a newly picked target, shown in the tree until the next save.
  const [pickedPath, setPickedPath] = useState<string | null>(null);
  const form = useForm<MenuItemForm>({
    resolver: useI18nResolver(menuItemFormSchema),
    defaultValues: {
      title: node.title,
      linkType: node.linkType,
      url: node.linkType === 'url' ? (node.url ?? '') : '',
      targetId: node.targetId,
      openInNewTab: node.openInNewTab,
    },
  });
  const linkType = form.watch('linkType');

  const submit = form.handleSubmit((values) => {
    const isUrl = values.linkType === 'url';
    const sameTarget =
      !isUrl && values.linkType === node.linkType && values.targetId === node.targetId;
    onSave({
      title: values.title,
      linkType: values.linkType,
      targetId: isUrl ? null : values.targetId,
      url: isUrl ? values.url : sameTarget ? node.url : pickedPath,
      openInNewTab: values.openInNewTab,
      targetMissing: sameTarget ? node.targetMissing : false,
    });
    onClose();
  });

  return (
    <Form {...form}>
      <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('menus:item.title')}</FormLabel>
              <FormControl>
                <Input autoFocus {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="linkType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('menus:item.linkType')}</FormLabel>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  form.setValue('targetId', null);
                  form.clearErrors(['url', 'targetId']);
                }}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(MenuLinkType).map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`menus:linkType.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {linkType === 'url' ? (
          <FormField
            control={form.control}
            name="url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('menus:item.url')}</FormLabel>
                <FormControl>
                  <Input className="font-mono text-[13px]" placeholder="/pages/about" {...field} />
                </FormControl>
                <FormDescription>{t('menus:item.urlHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : (
          <FormField
            control={form.control}
            name="targetId"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t(`menus:linkType.${linkType}`)}</FormLabel>
                <TargetIdField
                  storeId={storeId}
                  kind={linkType}
                  value={field.value}
                  clearable={false}
                  invalid={fieldState.invalid}
                  label={t(`menus:targetNoun.${linkType}`)}
                  onChange={(option) => {
                    field.onChange(option?.id ?? null);
                    setPickedPath(option?.detail ?? null);
                    if (option && !form.getValues('title').trim()) {
                      form.setValue('title', option.label, { shouldValidate: true });
                    }
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="openInNewTab"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center gap-3">
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <FormLabel>{t('menus:item.openInNewTab')}</FormLabel>
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit">{isNew ? t('menus:item.add') : t('menus:item.apply')}</Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/** Edits one item in the local tree; nothing is sent until the tree is saved. */
export function MenuItemDialog({
  storeId,
  node,
  isNew,
  open,
  onOpenChange,
  onSave,
}: MenuItemDialogProps) {
  const { t } = useTranslation('menus');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isNew ? t('item.newTitle') : t('item.editTitle')}</DialogTitle>
          <DialogDescription>{t('item.description')}</DialogDescription>
        </DialogHeader>
        <ItemFormBody
          storeId={storeId}
          node={node}
          isNew={isNew}
          onClose={() => onOpenChange(false)}
          onSave={onSave}
        />
      </DialogContent>
    </Dialog>
  );
}
